/**
 * Utility functions để đảm bảo Krpano đã sẵn sàng trước khi thực hiện các thao tác
 */

export interface KrpanoWaitOptions {
  maxAttempts?: number;
  interval?: number;
  checkFunction?: (krpano: any) => boolean;
}

export interface SceneInfo {
  sceneName: string;
  krpano: any;
}

/**
 * Đợi Krpano instance sẵn sàng
 */
export const waitForKrpano = (options: KrpanoWaitOptions = {}): Promise<any> => {
  const {
    maxAttempts = 30,
    interval = 300,
    checkFunction = (krpano) => krpano && krpano.get && krpano.get("xml.scene")
  } = options;

  return new Promise((resolve, reject) => {
    let attempts = 0;

    const check = () => {
      attempts++;

      // Kiểm tra global window.krpano trước
      if (window.krpano && checkFunction(window.krpano)) {
        // console.log(`✅ Global Krpano ready after ${attempts} attempts`);
        resolve(window.krpano);
        return;
      }

      // Fallback: kiểm tra DOM element
      const krpanoElement = document.getElementById("panorama");
      if (krpanoElement && checkFunction(krpanoElement)) {
        // console.log(`✅ DOM Krpano ready after ${attempts} attempts`);
        window.krpano = krpanoElement as any;
        resolve(krpanoElement);
        return;
      }

      if (attempts >= maxAttempts) {
        const error = `❌ Krpano not ready after ${maxAttempts} attempts`;
        console.error(error);
        reject(new Error(error));
        return;
      }

      setTimeout(check, interval);
    };

    check();
  });
};

/**
 * Thực hiện hàm Krpano một cách an toàn, đợi cho đến khi Krpano sẵn sàng
 */
export const safeKrpanoCall = async (
  callback: (krpano: any) => any,
  options: KrpanoWaitOptions = {}
): Promise<any> => {
  try {
    const krpano = await waitForKrpano(options);
    return callback(krpano);
  } catch (error) {
    console.error('SafeKrpanoCall failed:', error);
    throw error;
  }
};

/**
 * Kiểm tra xem Krpano đã sẵn sàng chưa (không đợi)
 */
export const isKrpanoReady = (): boolean => {
  try {
    if (window.krpano && window.krpano.get && window.krpano.get("xml.scene")) {
      return true;
    }

    const krpanoElement = document.getElementById("panorama");
    if (krpanoElement && (krpanoElement as any).get && (krpanoElement as any).get("xml.scene")) {
      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Đợi và lấy scene name đầu tiên từ Krpano
 */
export const waitForFirstScene = (options: KrpanoWaitOptions = {}): Promise<SceneInfo> => {
  const customCheck = (krpano: any) => {
    try {
      const sceneName = krpano.get("xml.scene");
      return sceneName && sceneName.trim() !== "";
    } catch {
      return false;
    }
  };

  return new Promise(async (resolve, reject) => {
    try {
      const krpano = await waitForKrpano({
        ...options,
        checkFunction: customCheck
      });

      const sceneName = krpano.get("xml.scene");
      //   console.log(`✅ First scene ready: ${sceneName}`);

      resolve({
        sceneName,
        krpano
      });
    } catch (error) {
      console.error('❌ Failed to get first scene:', error);
      reject(error);
    }
  });
};

/**
 * Đợi scene cụ thể được load hoàn toàn
 */
export const waitForSceneComplete = (sceneName: string, options: KrpanoWaitOptions = {}): Promise<any> => {
  const customCheck = (krpano: any) => {
    try {
      const currentScene = krpano.get("xml.scene");
      const isSceneReady = currentScene === sceneName;

      // Kiểm tra thêm xem scene đã load hoàn tất chưa
      if (isSceneReady) {
        const loadProgress = krpano.get("display.loadingprogress");
        return loadProgress === undefined || loadProgress === 1;
      }

      return false;
    } catch {
      return false;
    }
  };

  return waitForKrpano({
    ...options,
    checkFunction: customCheck
  });
};

/**
 * Lấy scene name hiện tại một cách an toàn
 */
export const getCurrentScene = (): string | null => {
  try {
    if (window.krpano && window.krpano.get) {
      return window.krpano.get("xml.scene");
    }

    const krpanoElement = document.getElementById("panorama");
    if (krpanoElement && (krpanoElement as any).get) {
      return (krpanoElement as any).get("xml.scene");
    }

    return null;
  } catch (error) {
    console.warn('Failed to get current scene:', error);
    return null;
  }
};
